import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  pimWorkflowDetailQuerySchema,
  pimWorkflowDetailSchema,
  pimWorkflowListQuerySchema,
  pimWorkflowListSchema,
  routes,
} from "@nova-ai/shared"
import { z } from "zod"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export const workflowHistorySearchSchema = z.object({
  ...pimWorkflowListQuerySchema.shape,
  receiptId: z.coerce.number().int().positive().optional(),
  targetAfter: pimWorkflowDetailQuerySchema.shape.targetAfter,
  attemptAfter: pimWorkflowDetailQuerySchema.shape.attemptAfter,
})
export type WorkflowHistorySearch = z.infer<typeof workflowHistorySearchSchema>
const POLL_WINDOW_MS = 5 * 60_000

export function workflowHistoryPollInterval(pending: boolean, startedAt: number, now: number) {
  return pending && now - startedAt < POLL_WINDOW_MS ? 15_000 : false
}

export function usePimWorkflowHistory(search: WorkflowHistorySearch) {
  const [startedAt, setStartedAt] = useState(() => Date.now())
  const listFilter = pimWorkflowListQuerySchema.parse(search)
  const detailFilter = pimWorkflowDetailQuerySchema.parse(search)
  const list = useQuery({
    queryKey: queryKeys.pimWorkflowHistory(listFilter),
    queryFn: async ({ signal }) => {
      const result = await apiFetch<unknown>(routes.pimEvents.history(), {
        signal,
        query: listFilter,
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return pimWorkflowListSchema.parse(result.value)
    },
    refetchInterval: (query) =>
      workflowHistoryPollInterval(
        search.receiptId === undefined &&
          (query.state.data?.receipts.some((receipt) => receipt.summary.pending) ?? false),
        startedAt,
        Date.now()
      ),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: 1,
  })
  const detail = useQuery({
    queryKey: queryKeys.pimWorkflowHistoryDetail(search.receiptId, detailFilter),
    enabled: search.receiptId !== undefined,
    queryFn: async ({ signal }) => {
      const result = await apiFetch<unknown>(
        routes.pimEvents.historyDetail({ receiptId: String(search.receiptId) }),
        { signal, query: detailFilter }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return pimWorkflowDetailSchema.parse(result.value)
    },
    refetchInterval: (query) =>
      workflowHistoryPollInterval(
        query.state.data?.receipt.summary.pending ?? false,
        startedAt,
        Date.now()
      ),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: 1,
  })
  return {
    list,
    detail,
    refresh: () => {
      setStartedAt(Date.now())
      void list.refetch()
      if (search.receiptId !== undefined) {
        void detail.refetch()
      }
    },
  }
}
